import React, { useEffect, useMemo, useState } from "react"
import useEventListener from "../hooks/use-event-listener"
import useThrottledFn from "../hooks/use-throttled-fn"

export const ClosestEditableContext = React.createContext({})

function getOffset(element) {
  const rect = element.getBoundingClientRect()
  const body = document.body

  return {
    top: rect.top + body.scrollTop + document.documentElement.scrollTop,
    left: rect.left + body.scrollLeft + document.documentElement.scrollLeft
  }
}

function getBuffer(element) {
  if (element.dataset.active == "true") {
    // if the element is active, make the element seem closer
    // so it takes moving further away to deactivate it.
    return -15
  } else if (element.dataset.empty == "false") {
    // if the element is active, make the element seem further away
    // so other close elements are more likely to be activated.
    return 10
  }
  return 0
}

function getDistance(mouse, element) {
  const offset = getOffset(element)

  const yDistance = mouse.y - (offset.top + element.offsetHeight / 2)
  const xDistance = mouse.x - (offset.left + element.offsetWidth / 2)

  // Apply a weight to the distance calculations. Under the current
  // settings below, this has the effect of triggering content areas
  // to open if the mouse is close on the Y, but further on the X.
  const xDistanceWeight = 0.1
  const yDistanceWeight = 1

  return Math.floor(
    Math.sqrt(
      Math.pow(xDistance * xDistanceWeight, 2) +
        Math.pow(yDistance * yDistanceWeight, 2)
    )
  )
}

function ClosestEditableDetector({ children }) {
  const [elements, setElements] = useState([])
  const [closestElement, setClosestElement] = useState(null)
  const [closestDistance, setClosestDistance] = useState(0)

  // The "proper" way for this to work is probably to have each Editable
  // component register its ref and pass it back up here. For now, we'll just
  // rely on querySelectorAll.
  useEffect(() => {
    setElements(document.querySelectorAll("[data-editable]"))
  }, [])

  const context = useMemo(
    () => ({
      closest: closestElement,
      distance: closestDistance
    }),
    [closestElement, closestDistance]
  )

  const onMouseMove = useThrottledFn(
    event => {
      requestAnimationFrame(() => {
        const mouse = {
          x: event.pageX,
          y: event.pageY
        }

        let closest

        elements.forEach(element => {
          const buffer = getBuffer(element)
          const distance = getDistance(mouse, element) + buffer

          if (!closest || distance < closest.distance) {
            closest = {
              distance,
              element: element.dataset.editable
            }
          }
        })

        if (closest) {
          setClosestElement(closest.element)
          setClosestDistance(closest.distance)
        }
      })
    },
    400,
    { leading: false, trailing: true }
  )

  useEventListener(window, "mousemove", event => {
    onMouseMove(event)
  })

  return (
    <ClosestEditableContext.Provider value={context}>
      {children}
    </ClosestEditableContext.Provider>
  )
}

export default ClosestEditableDetector
